// Small shared building blocks from the Product Design System: status pill, due label,
// empty state, skeletons and the per-item task track.
import { Skeleton, Tooltip } from 'antd';
import type { ReactNode } from 'react';
import { formatDate, formatDateTime, formatDue, formatDuration } from './format';
import type { StatusInfo, StatusTone } from './status';
import { taskStatus } from './status';
import type { DepartmentTask } from './types';

export function StatusPill({ status, tone, children }: { status?: StatusInfo; tone?: StatusTone; children?: ReactNode }) {
  const resolvedTone = status?.tone ?? tone ?? 'neutral';
  return (
    <span className={`ua-pill ua-pill--${resolvedTone}`}>
      {resolvedTone === 'danger' ? <span aria-hidden="true">×</span> : <span className="ua-pill-dot" aria-hidden="true" />}
      {status?.label ?? children}
    </span>
  );
}

/** Late is the one red thing on a card or row. */
export function DueText({ date, done = false, prefix }: { date: string; done?: boolean; prefix?: string }) {
  const due = formatDue(date);
  const tone = done ? 'normal' : due.tone;
  // Finished work is no longer "late" — show the date it was due.
  if (done) return <span className="ua-due ua-due--normal">{prefix ? `${prefix} ` : ''}{formatDate(date)}</span>;
  return (
    <span className={`ua-due ua-due--${tone}`}>
      {tone === 'late' && <span className="ua-due-dot" aria-hidden="true" />}
      {prefix && tone !== 'late' ? `${prefix} ` : ''}{due.label}
    </span>
  );
}

export function OrderNumber({ children }: { children: ReactNode }) {
  return <span className="ua-mono ua-order-number">{children}</span>;
}

/** Explain why it's empty and where work comes from, with the ⊥ mark. */
export function EmptyState({
  title,
  description,
  action,
  compact = false,
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div className={`ua-empty${compact ? ' ua-empty--compact' : ''}`}>
      <span className="ua-empty-mark" aria-hidden="true"><i /></span>
      <p className="ua-empty-title">{title}</p>
      {description && <p className="ua-empty-description">{description}</p>}
      {action && <div className="ua-empty-action">{action}</div>}
    </div>
  );
}

export function CardGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="zen-task-grid" aria-busy="true" aria-label="Loading">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="ua-skeleton-card">
          <Skeleton active title={{ width: '60%' }} paragraph={{ rows: 3, width: ['90%', '75%', '40%'] }} />
        </div>
      ))}
    </div>
  );
}

export function TableSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <div className="ua-skeleton-table" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton.Input key={index} active block size="small" />
      ))}
    </div>
  );
}

export function PageSkeleton() {
  return (
    <div className="ua-skeleton-page" aria-busy="true" aria-label="Loading">
      <Skeleton active title={{ width: '30%' }} paragraph={{ rows: 2 }} />
      <Skeleton active paragraph={{ rows: 6 }} />
    </div>
  );
}

function trackStep(task: DepartmentTask): 'done' | 'current' | 'upcoming' {
  if (task.status === 'completed') return 'done';
  if (task.status === 'started') return 'current';
  return 'upcoming';
}

function trackTooltip(task: DepartmentTask): string {
  const status = taskStatus(task).label;
  const parts = [`${task.departmentName} · ${status}`];
  if (task.startedAt) parts.push(`Started ${formatDateTime(task.startedAt)}${task.pickedUpByName ? ` by ${task.pickedUpByName}` : ''}`);
  if (task.finishedAt) parts.push(`Finished ${formatDateTime(task.finishedAt)}${task.finishedByName ? ` by ${task.finishedByName}` : ''}`);
  if (task.startedAt && task.finishedAt) {
    parts.push(`Took ${formatDuration(new Date(task.finishedAt).getTime() - new Date(task.startedAt).getTime())}`);
  }
  return parts.join('\n');
}

/** Design → selected departments → Delivery. Filled = done, ringed = started, hollow = waiting or pending. */
export function TaskTrack({ tasks }: { tasks: DepartmentTask[] }) {
  return (
    <ol className="ua-track" aria-label="Task progress">
      {tasks.map((task) => {
        const step = trackStep(task);
        return (
          <li key={task.id} className={`ua-track-step ua-track-step--${step}`}>
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{trackTooltip(task)}</span>}>
              <span className="ua-track-node" tabIndex={0} aria-label={`${task.departmentName}: ${taskStatus(task).label}`} />
            </Tooltip>
            <span className="ua-track-label">{task.departmentName}</span>
            <span className="ua-track-meta">
              {step === 'current' ? task.pickedUpByName ?? 'Started' : step === 'done' ? task.finishedByName ?? 'Done' : task.availableAt ? 'Pending' : 'Waiting'}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
